import React from 'react'
import {
  DARK_MODE_ENABLED,
  applyResolvedTheme,
  getSystemTheme,
  readStoredMode,
  resolveTheme,
  subscribeToSystemTheme,
  writeStoredMode,
  type ResolvedTheme,
  type ThemeMode,
} from './theme-mode'

export interface ThemeModeContextValue {
  /** What the user chose. 'system' is a real, persisted-as-absent state. */
  mode: ThemeMode
  /** What is actually applied to <html> right now. Never 'system'. */
  resolvedTheme: ResolvedTheme
  /** The current OS preference, tracked live. */
  systemTheme: ResolvedTheme
  /** Change the mode. Persists 'light'/'dark'; clears storage for 'system'. */
  setMode: (mode: ThemeMode) => void
}

const ThemeModeContext = React.createContext<ThemeModeContextValue | null>(null)
ThemeModeContext.displayName = 'ThemeModeContext'

/**
 * Owns the theme mode: reads the persisted choice, tracks the OS preference
 * live, and mirrors the result onto <html data-theme>.
 *
 * First paint is handled by public/theme-boot.js, which applies the same
 * resolution before the bundle parses. This provider re-applies the identical
 * value on mount (idempotent), then owns every change after that.
 */
export function ThemeModeProvider({children}: {children: React.ReactNode}) {
  const [mode, setModeState] = React.useState<ThemeMode>(readStoredMode)
  const [systemTheme, setSystemTheme] = React.useState<ResolvedTheme>(getSystemTheme)

  // While dark mode is gated off, always render light — never follow the OS or a stored
  // 'dark'. `resolveTheme` stays pure (and tested) for the day the switch flips.
  const resolvedTheme = DARK_MODE_ENABLED ? resolveTheme(mode, systemTheme) : 'light'

  // Follow the OS live, not just at first paint. The listener stays mounted
  // in every mode so `systemTheme` is accurate the instant the user switches
  // back to 'system'; `resolvedTheme` simply ignores it while a choice is set.
  React.useEffect(() => subscribeToSystemTheme(setSystemTheme), [])

  // Layout effect: the attribute lands before the browser paints this commit,
  // so a mode change never shows one frame of the previous theme.
  React.useLayoutEffect(() => {
    applyResolvedTheme(resolvedTheme)
  }, [resolvedTheme])

  const setMode = React.useCallback((next: ThemeMode) => {
    writeStoredMode(next)
    setModeState(next)
    // Re-read the OS at the moment of the switch: if the preference changed
    // while an explicit choice was masking it, 'system' must land on the
    // current value rather than a stale one.
    if (next === 'system') setSystemTheme(getSystemTheme())
  }, [])

  const value = React.useMemo<ThemeModeContextValue>(
    () => ({mode, resolvedTheme, systemTheme, setMode}),
    [mode, resolvedTheme, systemTheme, setMode],
  )

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>
}

/**
 * Read and set the theme. Must be called under <UiThemeProvider> (which mounts
 * ThemeModeProvider).
 *
 *   const {mode, resolvedTheme, setMode} = useThemeMode()
 *   <button onClick={() => setMode('dark')}>Dark</button>
 */
export function useThemeMode(): ThemeModeContextValue {
  const context = React.useContext(ThemeModeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider (see UiThemeProvider)')
  }
  return context
}

export {ThemeModeContext}
